import type React from 'react';
import type { FC } from 'react';

import classNames from 'classnames';
import { pick } from 'lodash-es';

import {
  type AriaProps,
  ariaToReactProps,
} from '@iconik-io/shared-lib/src/react/a11y/aria';

import { useFieldSizingFallback } from '../../hooks/use_field_sizing_fallback';
import { ui } from '../../utils/helpers';
import type { FormFieldEvent } from '../forms/types';

import styles from './textarea.module.css';

export const TEXTAREA_PROPS = [
  'id',
  'name',
  'value',
  'placeholder',
  'disabled',
  'onKeyDown',
] as const;

export type ValueType = string;

export type TextAreaProps = {
  id?: string;
  name?: string;
  value?: ValueType;
  className?: string;
  autoSize?: boolean;
  error?: boolean;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onChange?: (value: ValueType, event?: FormFieldEvent) => void;
  onBlur?: (value: ValueType, event: FormFieldEvent) => void;
  placeholder?: string;
  disabled?: boolean;
  aria?: AriaProps;
};

const TextArea: FC<TextAreaProps> = (props) => {
  const {
    error,
    autoSize = true,
    value,
    onChange,
    onBlur,
    onKeyDown,
    name,
    aria,
  } = props;
  const textareaProps = pick(props, TEXTAREA_PROPS);
  const autoSizeRef = useFieldSizingFallback(autoSize, value);

  const commonProps = {
    ...ariaToReactProps(aria),
    ...ui({ 'input-name': name }),
    className: classNames(styles.root, {
      [styles.error]: error,
      [styles.auto_size]: autoSize,
    }),
    onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) =>
      onChange?.(event.target.value, event),
    onBlur: (event: React.FocusEvent<HTMLTextAreaElement>) =>
      onBlur?.(event.target.value, event),
    onKeyDown,
  };
  return <textarea ref={autoSizeRef} {...commonProps} {...textareaProps} />;
};

export default TextArea;
